"use client"

import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useSession } from "next-auth/react"
import { useRouter } from "next/navigation"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Header } from "@/components/header"
import { ProtectedPDFReader } from "@/components/protected-pdf-reader"
import { CompactBuyButtons } from "@/components/buy-buttons"
import { Download, BookOpen, Headphones, ArrowLeft, Eye, Play, Loader2, Zap, Layers, Sparkles, Pause, Bookmark, BookmarkCheck } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import { R2Image } from "@/components/r2-image"
import Link from "next/link"
import { useState, useEffect } from "react"
import { SUMMARY_FORMATS, type SummaryFormatKey, getSummaryFormatLabel } from "@/lib/summary-formats"
import { useAudioPlayer } from "@/components/audio-player-provider"
import { isFreeMode } from "@/lib/feature-flags"
import { ReviewForm } from "@/components/review-form"
import { ReviewsList } from "@/components/reviews-list"
import { StarRating } from "@/components/star-rating"

interface Book {
  id: string
  title: string
  author: string
  description?: string
  genre?: string
  coverUrl?: string
  summary_5mins?: string
  summary_15mins?: string
  summary_chapter?: string
  podcastUrl?: string
  isbn?: string
  publishedDate?: string
  pageCount?: number
  language?: string
}

export function BookDetailsClient({ bookId, bookSlug }: { bookId: string; bookSlug: string }) {
  const router = useRouter()
  const { data: session } = useSession()
  const [viewingPDF, setViewingPDF] = useState<{url: string, title: string} | null>(null)
  const [downloadingItems, setDownloadingItems] = useState<Set<string>>(new Set())
  const [isCheckingPodcast, setIsCheckingPodcast] = useState(false)
  const [podcastError, setPodcastError] = useState<string | null>(null)
  const [bookmarkActionLoading, setBookmarkActionLoading] = useState(false)
  const [editingReview, setEditingReview] = useState<{id: string, rating: number, comment?: string | null} | null>(null)
  const [showReviewForm, setShowReviewForm] = useState(false)
  const audioPlayer = useAudioPlayer()
  const freeMode = isFreeMode
  const queryClient = useQueryClient()
  const isCurrentPodcast = audioPlayer.currentTrack?.meta?.bookId === bookId && audioPlayer.currentTrack?.source === "podcast"

  const { data: bookmarkStatus } = useQuery<{ bookmarked: boolean }>({
    queryKey: ["bookmark", bookId],
    queryFn: async () => {
      const response = await fetch(`/api/bookmarks?bookId=${bookId}`)
      if (!response.ok) {
        throw new Error("Failed to fetch bookmark status")
      }
      return response.json()
    },
    enabled: !!session && !!bookId,
    staleTime: 60 * 1000,
  })

  const isBookmarked = bookmarkStatus?.bookmarked ?? false

  const summaryOptionCards: Array<{
    format: SummaryFormatKey
    icon: LucideIcon
    accentClass: string
    iconClass: string
  }> = [
    {
      format: "5min",
      icon: Zap,
      accentClass: "bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/10 border-amber-200 dark:border-amber-800",
      iconClass: "bg-white/80 text-amber-600 shadow-sm",
    },
    {
      format: "10min",
      icon: Layers,
      accentClass: "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/10 border-blue-200 dark:border-blue-800",
      iconClass: "bg-white/80 text-blue-600 shadow-sm",
    },
    {
      format: "15min",
      icon: Sparkles,
      accentClass: "bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/10 border-emerald-200 dark:border-emerald-800",
      iconClass: "bg-white/80 text-emerald-600 shadow-sm",
    },
  ]

  // Track book visit
  useEffect(() => {
    const trackVisit = async () => {
      const userId = (session?.user as { id: string })?.id
      if (!userId || !bookId) return
      
      try {
        await fetch('/api/books/visit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ bookId: bookId }),
        })
      } catch (error) {
        console.error('Failed to track visit:', error)
      }
    }

    trackVisit()
  }, [session?.user, bookId])

  const { data: book, isLoading, error } = useQuery<Book>({
    queryKey: ["book", bookId],
    queryFn: async () => {
      const response = await fetch(`/api/books/${bookId}`)
      if (!response.ok) {
        throw new Error("Failed to fetch book")
      }
      return response.json()
    },
  })

  const handleDownload = async (type: "summary" | "podcast", format?: string) => {
    if (!session) {
      router.push(`/auth/signin?callbackUrl=${encodeURIComponent(window.location.pathname)}`)
      return
    }

    const downloadKey = `${type}${format ? `_${format}` : ''}`
    
    try {
      // Show loading state
      setDownloadingItems(prev => new Set(prev).add(downloadKey))
      
      const response = await fetch(`/api/books/${bookId}/download`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ type, format }),
      })

      if (!response.ok) {
        const error = await response.json()
        if (!freeMode && response.status === 402) {
          router.push("/subscription")
        } else if (response.status === 403) {
          // Download limit reached
          if (error.remainingDownloads !== undefined) {
            const limitMsg = error.limit === -1 
              ? "You have unlimited downloads!" 
              : `Download limit: ${error.usedDownloads}/${error.limit} books this month.\n\n${error.remainingDownloads > 0 ? `${error.remainingDownloads} downloads remaining.` : 'Downloads are temporarily unavailable. Please try again soon.'}`
            alert(error.error + "\n\n" + limitMsg)
          } else {
            alert(error.error || "Download limit reached")
          }
        } else {
          alert(error.message || error.error || "Failed to download")
        }
        return
      }

      const blob = await response.blob()
      console.log('Download blob:', blob.type, blob.size)
      
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      
      // Determine file extension from blob type or default
      let extension = type === "summary" ? "txt" : "mp3"
      if (type === "podcast") {
        // Try to detect from blob type
        if (blob.type.includes("m4a") || blob.type.includes("mp4")) {
          extension = "m4a"
        } else if (blob.type.includes("wav")) {
          extension = "wav"
        } else if (blob.type.includes("ogg")) {
          extension = "ogg"
        } else if (blob.type.includes("aac")) {
          extension = "aac"
        } else if (blob.type.includes("mpeg") || blob.type.includes("mp3")) {
          extension = "mp3"
        }
      }
      
      const formatSuffix = format ? `_${format}` : ""
      a.download = `${book?.title}_${type}${formatSuffix}.${extension}`
      
      console.log('Downloading file:', a.download)
      
      // For text files, also provide option to open in new tab
      if (type === "summary") {
        const openInNewTab = confirm("Would you like to open the summary in a new tab instead of downloading?")
        if (openInNewTab) {
          window.open(url, '_blank')
          return
        }
      }
      
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error("Download error:", error)
      alert("Failed to download file")
    } finally {
      // Hide loading state
      setDownloadingItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(downloadKey)
        return newSet
      })
    }
  }

  const handleViewPDF = async (type: "summary", format?: string) => {
    if (!session) {
      router.push(`/auth/signin?callbackUrl=${encodeURIComponent(window.location.pathname)}`)
      return
    }

    try {
      const params = new URLSearchParams({
        type,
        ...(format && { format }),
        view: "true"
      })

      const pdfUrl = `/api/books/${bookId}/download?${params}`
      const title = `${book?.title} - ${format ? getSummaryFormatLabel(format) : "Complete"} Summary`
      setViewingPDF({ url: pdfUrl, title })
    } catch (error) {
      console.error("Error loading PDF:", error)
    }
  }

  const startPodcastPlayback = async () => {
    if (!session) {
      router.push(`/auth/signin?callbackUrl=${encodeURIComponent(window.location.pathname)}`)
      return
    }

    const audioUrl = `/api/books/${bookId}/download?type=podcast&play=true`
    setIsCheckingPodcast(true)
    setPodcastError(null)

    try {
      await audioPlayer.playTrack({
        id: `${bookId}-podcast`,
        title: `${book?.title ?? "Podcast"}`,
        url: audioUrl,
        source: "podcast",
        meta: { bookId, bookSlug },
      })
    } catch (error: unknown) {
      console.error("Error starting podcast audio:", error)
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        setPodcastError("Playback was blocked by your mobile browser. Tap Play again to allow audio.")
      } else {
        setPodcastError("Unable to start the audio. Please try again.")
      }
    } finally {
      setIsCheckingPodcast(false)
    }
  }

  const toggleBookmark = async () => {
    if (!session) {
      router.push(`/auth/signin?callbackUrl=${encodeURIComponent(window.location.pathname)}`)
      return
    }

    if (bookmarkActionLoading) return

    setBookmarkActionLoading(true)
    try {
      const method = isBookmarked ? "DELETE" : "POST"
      const response = await fetch("/api/bookmarks", {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ bookId }),
      })

      if (!response.ok) {
        const error = await response.json()
        alert(error?.error || "Unable to update bookmark")
        return
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["bookmark", bookId] }),
        queryClient.invalidateQueries({ queryKey: ["bookmarks"] }),
      ])
    } catch (error) {
      console.error("Bookmark toggle error:", error)
      alert("Unable to update bookmark. Please try again.")
    } finally {
      setBookmarkActionLoading(false)
    }
  }


  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-muted rounded w-1/4 mb-4" />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-1">
                <div className="aspect-[3/4] bg-muted rounded-lg" />
              </div>
              <div className="lg:col-span-2 space-y-4">
                <div className="h-8 bg-muted rounded w-3/4" />
                <div className="h-4 bg-muted rounded w-1/2" />
                <div className="h-4 bg-muted rounded w-1/4" />
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded" />
                  <div className="h-4 bg-muted rounded" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !book) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Book not found</h1>
            <Button asChild>
              <Link href="/">
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Catalog
              </Link>
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <Button asChild variant="ghost" className="mb-6">
          <Link href="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Catalog
          </Link>
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Book Cover */}
          <div className="lg:col-span-1">
            <div className="aspect-[3/4] relative overflow-hidden rounded-lg bg-muted">
              {book.coverUrl ? (
                <R2Image
                  src={book.coverUrl}
                  alt={book.title}
                  fill
                  className="object-contain object-center"
                  sizes="(max-width: 1024px) 100vw, 33vw"
                  quality={95}
                  priority={true}
                  placeholder="blur"
                  blurDataURL="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R//2Q=="
                  fallbackIcon={<BookOpen className="h-24 w-24 text-muted-foreground" />}
                />
              ) : (
                <div className="w-full h-full bg-muted flex items-center justify-center">
                  <BookOpen className="h-24 w-24 text-muted-foreground" />
                </div>
              )}
            </div>
          </div>

          {/* Book Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <h1 className="text-3xl font-bold mb-2">{book.title}</h1>
                <p className="text-xl text-muted-foreground mb-4">by {book.author}</p>
                
                <div className="flex flex-wrap gap-2 mb-4">
                  {book.genre && (
                    book.genre.split(',').map((g, idx) => (
                      <Badge key={idx} variant="secondary">{g.trim()}</Badge>
                    ))
                  )}
                  {book.language && (
                    <Badge variant="outline">{book.language}</Badge>
                  )}
                  {book.pageCount && (
                    <Badge variant="outline">{book.pageCount} pages</Badge>
                  )}
                </div>

                {book.description && (
                  <div className="prose max-w-none">
                    <p className="text-muted-foreground leading-relaxed">
                      {book.description}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex flex-col items-start gap-2">
                <Button
                  variant={isBookmarked ? "default" : "outline"}
                  className="gap-2"
                  onClick={toggleBookmark}
                  disabled={bookmarkActionLoading}
                  aria-pressed={isBookmarked}
                >
                  {bookmarkActionLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : isBookmarked ? (
                    <BookmarkCheck className="h-4 w-4" />
                  ) : (
                    <Bookmark className="h-4 w-4" />
                  )}
                  {isBookmarked ? "Bookmarked" : "Save to bookmarks"}
                </Button>
                {!session && (
                  <p className="text-xs text-muted-foreground">
                    Sign in to keep your bookmarks
                  </p>
                )}
              </div>
            </div>

            {/* Download Options */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="h-5 w-5" />
                    Book Summary
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {summaryOptionCards.map(({ format, icon: OptionIcon, accentClass, iconClass }) => {
                      const meta = SUMMARY_FORMATS[format]
                      const downloadKey = `summary_${format}`
                      const isDownloading = downloadingItems.has(downloadKey)
                      return (
                        <div
                          key={format}
                          className={`rounded-xl border p-4 transition hover:shadow-md ${accentClass}`}
                        >
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="flex items-start gap-3">
                              <div className={`rounded-full p-2 ${iconClass}`}>
                                <OptionIcon className="h-5 w-5" />
                              </div>
                              <div>
                                {"durationLabel" in meta && meta.durationLabel && (
                                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                                    {meta.durationLabel}
                                  </p>
                                )}
                                <h4 className="text-lg font-semibold">{meta.label}</h4>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {meta.description}
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <Button 
                              onClick={() => handleViewPDF("summary", format)}
                              variant="outline"
                              className="w-full"
                            >
                              <Eye className="mr-2 h-4 w-4" />
                              Preview
                            </Button>
                            <Button 
                              onClick={() => handleDownload("summary", format)}
                              className="w-full"
                              disabled={isDownloading}
                              variant="default"
                            >
                              {isDownloading ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="mr-2 h-4 w-4" />
                              )}
                              {isDownloading ? "Downloading..." : "Download"}
                            </Button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Headphones className="h-5 w-5" />
                    Sipcast
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    Listen to an engaging podcast about this book
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        if (isCurrentPodcast) {
                          audioPlayer.togglePlayPause()
                        } else {
                          startPodcastPlayback()
                        }
                      }}
                      className="flex-1"
                      variant="outline"
                      disabled={isCheckingPodcast}
                    >
                      {isCheckingPodcast ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : isCurrentPodcast ? (
                        audioPlayer.isPlaying ? <Pause className="mr-2 h-4 w-4" /> : <Play className="mr-2 h-4 w-4" />
                      ) : (
                        <Play className="mr-2 h-4 w-4" />
                      )}
                      {isCheckingPodcast
                        ? "Loading..."
                        : isCurrentPodcast
                          ? audioPlayer.isPlaying
                            ? "Pause"
                            : "Resume"
                          : "Play"}
                    </Button>
                    <Button 
                      onClick={() => handleDownload("podcast")}
                      className="flex-1"
                      disabled={downloadingItems.has("podcast")}
                    >
                      {downloadingItems.has("podcast") ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Download className="mr-2 h-4 w-4" />
                      )}
                      {downloadingItems.has("podcast") ? "Downloading..." : "Download"}
                    </Button>
                  </div>
                  <div className="space-y-3 mt-3">
                    {podcastError && (
                      <p className="text-sm text-red-500">{podcastError}</p>
                    )}
                    <AffiliatePromo bookTitle={book.title} author={book.author} />
                  </div>
                </CardContent>
              </Card>
            </div>

          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-12">
          <ReviewsSection 
            bookId={bookId}
            editingReview={editingReview}
            onEdit={(review) => {
              setEditingReview(review)
              setShowReviewForm(true)
            }}
            onCancelEdit={() => {
              setEditingReview(null)
              setShowReviewForm(false)
            }}
            showReviewForm={showReviewForm}
            onToggleForm={() => setShowReviewForm(!showReviewForm)}
          />
        </div>

        {/* PDF Reader Modal */}
        {viewingPDF && (
          <ProtectedPDFReader
            pdfUrl={viewingPDF.url}
            title={viewingPDF.title}
            onClose={() => setViewingPDF(null)}
            bookSlug={bookSlug}
          />
        )}
      </div>
    </div>
  )
}

interface ReviewsSectionProps {
  bookId: string
  editingReview: {id: string, rating: number, comment?: string | null} | null
  onEdit: (review: {id: string, rating: number, comment?: string | null}) => void
  onCancelEdit: () => void
  showReviewForm: boolean
  onToggleForm: () => void
}

function ReviewsSection({ 
  bookId, 
  editingReview, 
  onEdit, 
  onCancelEdit, 
  showReviewForm, 
  onToggleForm 
}: ReviewsSectionProps) {
  const { data: session } = useSession()
  const queryClient = useQueryClient()

  // Fetch review stats
  const { data: reviewStats } = useQuery<{
    stats: {
      averageRating: number
      totalReviews: number
    }
  }>({
    queryKey: ["reviews", bookId, 1],
    queryFn: async () => {
      const response = await fetch(`/api/books/${bookId}/reviews?page=1&limit=1`)
      if (!response.ok) {
        throw new Error("Failed to fetch reviews")
      }
      return response.json()
    },
  })

  // Check if user has already reviewed
  const { data: userReview } = useQuery<{
    review?: {
      id: string
      rating: number
      comment?: string | null
    }
  }>({
    queryKey: ["user-review", bookId],
    queryFn: async () => {
      if (!session) return { review: undefined }
      const response = await fetch(`/api/books/${bookId}/reviews?page=1&limit=100`)
      if (!response.ok) {
        return { review: undefined }
      }
      const data = await response.json()
      const userId = (session.user as { id?: string })?.id
      if (!userId) return { review: undefined }
      const review = data.reviews.find((r: { user: { id: string } }) => r.user.id === userId)
      return { review: review ? { id: review.id, rating: review.rating, comment: review.comment } : undefined }
    },
    enabled: !!session,
  })

  const handleReviewSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ["reviews", bookId] })
    queryClient.invalidateQueries({ queryKey: ["user-review", bookId] })
    onToggleForm()
    onCancelEdit()
  }

  const stats = reviewStats?.stats || { averageRating: 0, totalReviews: 0 }
  const hasUserReview = !!userReview?.review && !editingReview

  return (
    <div className="space-y-6">
      {/* Reviews Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Customer Reviews</h2>
          {stats.totalReviews > 0 ? (
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <StarRating rating={stats.averageRating} size="lg" showValue />
                <span className="text-sm text-muted-foreground">
                  ({stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'})
                </span>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No reviews yet</p>
          )}
        </div>
        {session && !hasUserReview && !showReviewForm && (
          <Button onClick={onToggleForm}>
            Write a Review
          </Button>
        )}
      </div>

      {/* Review Form */}
      {(showReviewForm || editingReview) && (
        <ReviewForm
          bookId={bookId}
          existingReview={editingReview || undefined}
          onSuccess={handleReviewSuccess}
          onCancel={() => {
            onCancelEdit()
            onToggleForm()
          }}
        />
      )}

      {/* Reviews List */}
      <ReviewsList 
        bookId={bookId}
        onEdit={onEdit}
      />
    </div>
  )
}


interface AffiliatePromoProps {
  bookTitle: string
  author: string
}

function AffiliatePromo({ bookTitle, author }: AffiliatePromoProps) {
  return (
    <div className="p-3 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-950/30 dark:to-blue-950/30 rounded-lg border border-green-200 dark:border-green-800">
      <h3 className="text-sm font-semibold mb-1 text-green-900 dark:text-green-100">
        Want to read the full book?
      </h3>
      <p className="text-xs text-green-700 dark:text-green-300 mb-3">
        Support the author and get the complete reading experience
      </p>
      <CompactBuyButtons bookTitle={bookTitle} author={author} />
    </div>
  )
}
